import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Search, AlertTriangle, Package } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Freinage', 'Moteur', 'Suspension', 'Transmission', 'Électrique', 'Carrosserie', 'Filtres', 'Huiles', 'Pneus', 'Autre'];

const getStockClass = (stock, threshold) => {
  if (stock <= threshold) return 'bg-red-100 text-red-700';
  if (stock <= threshold * 2) return 'bg-amber-100 text-amber-700';
  return 'bg-green-100 text-green-700';
};

export default function ProductsModule({ authHeaders }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const emptyForm = { name: '', reference: '', category: '', purchase_price: 0, sell_price: 0, stock: 0, alert_threshold: 5, supplier: '', description: '' };
  const [form, setForm] = useState(emptyForm);

  const totalStock = useMemo(() => products.reduce((s, p) => s + p.stock, 0), [products]);
  const purchaseValue = useMemo(() => products.reduce((s, p) => s + p.stock * p.purchase_price, 0), [products]);
  const sellValue = useMemo(() => products.reduce((s, p) => s + p.stock * p.sell_price, 0), [products]);

  const fetchProducts = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (catFilter) params.category = catFilter;
    const res = await axios.get(`${API_URL}/api/crm/products`, { headers: authHeaders, params });
    setProducts(res.data);
  }, [search, catFilter, authHeaders]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) await axios.put(`${API_URL}/api/crm/products/${editing.id}`, form, { headers: authHeaders });
      else await axios.post(`${API_URL}/api/crm/products`, form, { headers: authHeaders });
      setShowForm(false);
      setEditing(null);
      fetchProducts();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    await axios.delete(`${API_URL}/api/crm/products/${id}`, { headers: authHeaders });
    fetchProducts();
  };

  const openEdit = (p) => {
    setForm({ name: p.name, reference: p.reference, category: p.category, purchase_price: p.purchase_price, sell_price: p.sell_price, stock: p.stock, alert_threshold: p.alert_threshold, supplier: p.supplier, description: p.description });
    setEditing(p);
    setShowForm(true);
  };

  const lowStockCount = products.filter(p => p.stock <= p.alert_threshold).length;

  return (
    <div data-testid="products-module">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex gap-3 flex-wrap items-center flex-1">
          <div className="relative flex-1 max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#DC2626] focus:outline-none" data-testid="product-search" />
          </div>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="product-category-filter">
            <option value="">Toutes catégories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {lowStockCount > 0 && (
            <span className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 px-3 py-1.5 rounded-full">
              <AlertTriangle size={14} /> {lowStockCount} stock bas
            </span>
          )}
        </div>
        <button onClick={() => { setForm(emptyForm); setEditing(null); setShowForm(true); }} className="flex items-center gap-2 bg-[#DC2626] text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700" data-testid="add-product-btn">
          <Plus size={16} /> Nouveau produit
        </button>
      </div>

      <div className="bg-white rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3">Produit</th>
                <th className="text-left p-3 hidden md:table-cell">Réf.</th>
                <th className="text-left p-3 hidden md:table-cell">Catégorie</th>
                <th className="text-right p-3 hidden lg:table-cell">Achat HT</th>
                <th className="text-right p-3">Vente HT</th>
                <th className="text-center p-3">Stock</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className={`border-t hover:bg-gray-50 ${p.stock <= p.alert_threshold ? 'bg-amber-50/50' : ''}`}>
                  <td className="p-3">
                    <p className="font-medium">{p.name}</p>
                    {p.supplier && <p className="text-xs text-gray-400">{p.supplier}</p>}
                  </td>
                  <td className="p-3 hidden md:table-cell font-mono text-gray-500 text-xs">{p.reference || '-'}</td>
                  <td className="p-3 hidden md:table-cell"><span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{p.category || '-'}</span></td>
                  <td className="p-3 text-right hidden lg:table-cell text-gray-600">{p.purchase_price?.toFixed(2)}€</td>
                  <td className="p-3 text-right font-semibold">{p.sell_price?.toFixed(2)}€</td>
                  <td className="p-3 text-center">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${getStockClass(p.stock, p.alert_threshold)}`}>
                      {p.stock <= p.alert_threshold && <AlertTriangle size={12} />}
                      {p.stock}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-amber-50 rounded text-amber-600" title="Modifier"><Pencil size={15} /></button>
                      <button onClick={() => handleDelete(p.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Supprimer"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!products.length && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-gray-400">
                    <Package size={32} className="mx-auto mb-2 text-gray-300" />
                    Aucun produit trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Margin summary */}
      {products.length > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{products.length}</p>
            <p className="text-xs text-gray-500">Références</p>
          </div>
          <div className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{totalStock}</p>
            <p className="text-xs text-gray-500">Pièces en stock</p>
          </div>
          <div className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{purchaseValue.toLocaleString('fr-FR')}€</p>
            <p className="text-xs text-gray-500">Valeur stock (achat)</p>
          </div>
          <div className="bg-white rounded-xl border p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{sellValue.toLocaleString('fr-FR')}€</p>
            <p className="text-xs text-gray-500">Valeur stock (vente)</p>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b"><h3 className="font-semibold text-lg">{editing ? 'Modifier le produit' : 'Nouveau produit'}</h3></div>
            <form onSubmit={handleSave} className="p-5 space-y-3">
              <input required placeholder="Nom du produit *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="product-name-input" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Référence" value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" />
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="border rounded-lg px-3 py-2 text-sm">
                  <option value="">Catégorie</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Prix achat HT (€)</label>
                  <input type="number" step="0.01" min="0" value={form.purchase_price} onChange={e => setForm({ ...form, purchase_price: parseFloat(e.target.value) || 0 })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Prix vente HT (€)</label>
                  <input type="number" step="0.01" min="0" value={form.sell_price} onChange={e => setForm({ ...form, sell_price: parseFloat(e.target.value) || 0 })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              {form.purchase_price > 0 && form.sell_price > 0 && (
                <p className="text-xs text-gray-500">Marge: <span className="font-semibold text-green-600">{((form.sell_price - form.purchase_price) / form.purchase_price * 100).toFixed(1)}%</span> ({(form.sell_price - form.purchase_price).toFixed(2)}€)</p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Stock actuel</label>
                  <input type="number" min="0" value={form.stock} onChange={e => setForm({ ...form, stock: parseInt(e.target.value) || 0 })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Seuil d'alerte</label>
                  <input type="number" min="0" value={form.alert_threshold} onChange={e => setForm({ ...form, alert_threshold: parseInt(e.target.value) || 0 })} className="w-full border rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <input placeholder="Fournisseur" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />
              <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
              <div className="flex justify-end gap-3 pt-3">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 text-sm bg-[#DC2626] text-white rounded-lg hover:bg-red-700" data-testid="save-product-btn">{editing ? 'Enregistrer' : 'Créer le produit'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
